import { useState } from 'react';
import {
  Create,
  SimpleForm,
  TextInput,
  ArrayInput,
  SimpleFormIterator,
  required,
  useNotify,
  useRedirect,
  useDataProvider,
} from 'react-admin';
import {
  Typography,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  Tooltip,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';

export const OAuthClientCreate = () => {
  const [showSecretDialog, setShowSecretDialog] = useState(false);
  const [createdClient, setCreatedClient] = useState<{
    clientId: string;
    clientSecret: string;
  } | null>(null);
  const [copied, setCopied] = useState(false);
  const notify = useNotify();
  const redirect = useRedirect();
  const dataProvider = useDataProvider();

  const handleSubmit = async (data: any) => {
    try {
      const result = await dataProvider.create('oauth-clients', { data });
      
      // If server returned clientSecret, show it
      if (result.data.clientSecret) {
        setCreatedClient({
          clientId: result.data.clientId,
          clientSecret: result.data.clientSecret,
        });
        setShowSecretDialog(true);
      } else {
        notify('OAuth client created successfully', { type: 'success' });
        redirect('list', 'oauth-clients');
      }
    } catch (error: any) {
      notify(error.message || 'Failed to create OAuth client', { type: 'error' });
    }
  };

  const handleCopySecret = () => {
    if (createdClient?.clientSecret) {
      navigator.clipboard.writeText(createdClient.clientSecret);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      notify('Client secret copied to clipboard', { type: 'success' });
    }
  };

  const handleCloseDialog = () => {
    setShowSecretDialog(false);
    redirect('list', 'oauthClients');
  };

  return (
    <>
      <Create>
        <SimpleForm onSubmit={handleSubmit}>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Create a new OAuth2 client. The client secret will be{' '}
              <strong>generated automatically</strong> and displayed once after creation.
              Make sure to save it securely!
            </Typography>
          </Alert>

          <TextInput
            source="name"
            label="Client Name"
            validate={required()}
            fullWidth
          />
          <TextInput
            source="clientId"
            label="Client ID"
            validate={required()}
            fullWidth
            helperText="Unique identifier for this client (e.g., 'my-app')"
          />

          <ArrayInput
            source="redirectUris"
            label="Redirect URIs"
            validate={required()}
            helperText="One or more redirect URIs for OAuth callback"
          >
            <SimpleFormIterator inline>
              <TextInput source="" label="URI" helperText={false} fullWidth />
            </SimpleFormIterator>
          </ArrayInput>

          <ArrayInput
            source="allowedScopes"
            label="Allowed Scopes"
            defaultValue={['openid', 'profile', 'email']}
          >
            <SimpleFormIterator inline>
              <TextInput source="" label="Scope" helperText={false} />
            </SimpleFormIterator>
          </ArrayInput>
        </SimpleForm>
      </Create>

      {/* Secret Display Dialog */}
      <Dialog open={showSecretDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          🔐 OAuth Client Created Successfully
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Important:</strong> This is the only time the client secret will be shown.
              Copy and save it securely now!
            </Typography>
          </Alert>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Client ID
            </Typography>
            <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
              {createdClient?.clientId}
            </Typography>
          </Box>

          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Client Secret
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                bgcolor: 'grey.100',
                p: 2,
                borderRadius: 1,
                fontFamily: 'monospace',
                wordBreak: 'break-all',
              }}
            >
              <Typography variant="body1" sx={{ flex: 1, fontFamily: 'monospace' }}>
                {createdClient?.clientSecret}
              </Typography>
              <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
                <IconButton onClick={handleCopySecret} color={copied ? 'success' : 'default'}>
                  {copied ? <CheckIcon /> : <ContentCopyIcon />}
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCopySecret} startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}>
            {copied ? 'Copied!' : 'Copy Secret'}
          </Button>
          <Button onClick={handleCloseDialog} variant="contained" color="primary">
            I've Saved the Secret
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
